import React, { useState } from 'react'
import { NavLink, useParams } from 'react-router-dom'
import { useMutation, useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { toast } from 'react-toastify'
import { useStore } from 'effector-react'
import { Button, Modal, Page } from '../../../UI'
import { Option } from '../../../UI/Input'
import { defaultRoundStatistics, GetRoundsReq, Round, RoundsAPI } from '../../../../services/api/Rounds'
import { Routes } from '../../../enum'
import { getContent, getOptionsValue, orderDirectionOptions, toDollar } from '../../../utils'
import { defaultQuery } from '../../../hooks'
import { $defaults } from '../../../../states/Defaults/store'
import { $my } from '../../../../states/My/store'
import { Box, Paper, Stack, Typography } from '@mui/material'

const orderByOptions: Option<string>[] = [
  { key: 'createdAt', value: 'Время создания' },
  { key: 'startsAt', value: 'Начало' },
  { key: 'endsAt', value: 'Конец' },
]

const statusOptions: Option<string>[] = [
  { key: 'draft', value: 'Черновик' },
  { key: 'active', value: 'Активный' },
  { key: 'finished', value: 'Завершено' },
  { key: 'archived', value: 'Архив' },
]

interface RoundFormProps {
  projectId: string
  roundId: number
  onClose: () => void
  onSaved: () => void
}

const StatCard: React.FC<{ label: string, value: React.ReactNode, color: string, progress?: number }> = ({ label, value, color, progress }) => (
  <Paper
    elevation={1}
    sx={{
      px: 2.5,
      py: 1.5,
      borderRadius: '8px',
      borderLeft: `4px solid ${color}`,
      minWidth: 140,
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
      '&:hover': { boxShadow: 4, transform: 'translateY(-2px)' },
    }}
  >
    <Typography variant="body2" color="text.secondary">{label}</Typography>
    <Typography variant="h5" fontWeight={700}>{value}</Typography>
    {progress !== undefined &&
      <Box sx={{ mt: 1, height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.08)' }}>
        <Box
          sx={{
            height: '100%',
            borderRadius: 3,
            bgcolor: color,
            width: `${Math.min(progress, 100)}%`,
            transition: 'width 0.6s ease',
          }}
        />
      </Box>
    }
  </Paper>
)

const RoundForm: React.FC<RoundFormProps> = ({
  projectId,
  roundId,
  onClose,
  onSaved,
}) => {
  const defaults = useStore($defaults)
  const my = useStore($my)
  const get = useQuery(
    ['RoundsAPI.getRound', roundId],
    () => RoundsAPI.getRound({ id: roundId }),
  )
  const patch = useMutation(RoundsAPI.patchRound)

  const rolesOptions = (defaults?.users?.roles || []).map((o: Option<string>) => ({
    ...o,
    key: String(o.key).toUpperCase(),
  }))

  const stats = get.data?.data.data?.statistics || defaultRoundStatistics
  const data = get.data?.data.data

  const handlerSubmit = async (data: Round) => {
    const body = {
      projectId: Number(projectId),
      title: data.title,
      description: data.description,
      targetPrice: data.targetPrice,
      startsAt: data.startsAt,
      endsAt: data.endsAt,
      attachments: data.attachments || [],
      status: data.status,
      allowedRoles: (data.allowedRoles || []).map((r) => r.toUpperCase()),
      internalComment: data.internalComment,
    }
    await patch.mutateAsync({ ...body, id: roundId })
    toast.success('Сохранено')
    onSaved()
    onClose()
  }

  return <>
    <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: 'wrap' }}>
      <StatCard label="Цель" value={`${toDollar(data?.targetPrice || 0)} $`} color="#9c27b0" />
      <StatCard label="Пополнений" value={`${toDollar(stats.collectedPrice)} $`} color="#2e7d32" />
      <StatCard label="Прогресс" value={`${stats.percent || 0}%`} color="#ed6c02" progress={stats.percent} />
    </Stack>
    <Page
      variant="form"
      title={`Раунд #${roundId}`}
      button="Сохранить"
      loading={[get.isLoading, patch.isLoading]}
      value={{ ...get.data?.data.data, projectId: Number(projectId) } as Round}
      onSubmit={handlerSubmit}
      fields={[
        { name: 'projectId', type: 'number', label: 'Проект ID', disabled: true },
        { name: 'title', type: 'content', label: 'Заголовок', mode: 'text' },
        { name: 'description', type: 'content', label: 'Описание' },
        { name: 'attachments', type: 'file', label: 'Вложения', multiple: true, filename: `round::userId=${my?.id}` },
        { name: 'targetPrice', type: 'cent', label: 'Целевая цена' },
        { name: 'startsAt', type: 'date', label: 'Начало' },
        { name: 'endsAt', type: 'date', label: 'Конец' },
        { name: 'status', type: 'select', label: 'Статус', options: statusOptions },
        { name: 'allowedRoles', type: 'select', label: 'Доступность', multiple: true, options: rolesOptions },
        { name: 'internalComment', type: 'string', label: 'Комментарий (для админа)' },
        { name: 'createdAt', type: 'date', label: 'Создан', disabled: true },
        { name: 'updatedAt', type: 'date', label: 'Обновлён', disabled: true },
      ]}
    />
  </>
}

export const ProjectRounds: React.FC = () => {
  const { id } = useParams()
  const [query, setQuery] = useState<GetRoundsReq>({
    ...defaultQuery,
    projectId: id,
  })
  const [editId, setEditId] = useState<number | null>(null)

  const get = useQuery(
    ['RoundsAPI.getRounds', query],
    () => RoundsAPI.getRounds(query),
    { enabled: !!id },
  )
  const post = useMutation(RoundsAPI.postRound)

  const handlerCreate = async () => {
    const { data: { data: { id: roundId } } } = await post.mutateAsync({
      projectId: Number(id),
      status: 'draft',
      title: [],
      description: [],
      targetPrice: 0,
      startsAt: dayjs().toISOString(),
      endsAt: dayjs().add(1, 'month').toISOString(),
      attachments: [],
      allowedRoles: [],
      internalComment: '',
    })
    toast.success('Черновик раунда создан')
    get.refetch()
    setEditId(roundId)
  }

  return <>
    <Page
      variant="root"
      title="Раунды"
      actions={[
        { title: 'К проекту', type: 'link', to: `/${Routes.PROJECT}/${id}` },
        {
          title: 'Создать раунд',
          type: 'confirm',
          message: 'Вы уверены что хотите создать новый раунд?',
          onClick: handlerCreate,
          loading: post.isLoading,
        },
      ]}
      onChange={(values: Record<string, string>) => setQuery({ ...values, projectId: id })}
      loading={[get.isLoading]}
      rows={get.data?.data.data.items}
      total={get.data?.data.data.metadata.total}
      defaultFilter={{
        ...defaultQuery,
        projectId: id,
        orderBy: orderByOptions[0].key,
        orderDirection: orderDirectionOptions[0].key,
      }}
      filter={[
        {
          defaultExpanded: true,
          label: 'Фильтры',
          fields: [
            { name: 'status', type: 'select', label: 'Статус', options: statusOptions, isEmpty: true },
            { name: 'orderBy', type: 'select', label: 'Сортировать по', options: orderByOptions, isEmpty: true },
            { name: 'orderDirection', type: 'select', label: 'Упорядочить', options: orderDirectionOptions, isEmpty: true },
          ],
        },
      ]}
      col={[
        {
          id: 'id',
          label: 'ID',
          render: (roundId: number) =>
            <Button size="small" variant="text" onClick={() => setEditId(roundId)}>
              {roundId}
            </Button>,
        },
        { id: 'projectId', label: 'Проект', render: (pid: number) => <NavLink to={`/${Routes.PROJECT}/${pid}`}>{pid}</NavLink> },
        { id: 'title', label: 'Заголовок', render: (title) => getContent(title)?.slice(0, 30) },
        { id: 'targetPrice', label: 'Цель', render: (p: number) => `${toDollar(p)} $` },
        { id: 'status', label: 'Статус', render: (s: string) => getOptionsValue(statusOptions, s) },
        { id: 'attachments', label: 'Файлы', render: (files: string[] = []) => files.length },
        { id: 'startsAt', label: 'Начало', render: (d: string) => d && dayjs(d).format('DD.MM.YY HH:mm') },
        { id: 'endsAt', label: 'Конец', render: (d: string) => d && dayjs(d).format('DD.MM.YY HH:mm') },
        { id: 'internalComment', label: 'Коммент' },
        { id: 'createdAt', label: 'Создан', render: (d: string) => dayjs(d).format('DD.MM.YY HH:mm') },
      ]}
    />
    <Modal
      open={!!editId}
      onClose={() => setEditId(null)}
      minWidth={600}
    >
      {!!editId &&
        <RoundForm
          projectId={id as string}
          roundId={editId}
          onClose={() => setEditId(null)}
          onSaved={() => get.refetch()}
        />
      }
    </Modal>
  </>
}
